import { CommonModule } from '@angular/common';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { Component, EnvironmentInjector, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
    CategoriesService,
    CategoryDto,
    CategoryProductCount,
} from '../../services/categories/categories.service';
import { ModalService } from '../../services/design/modal.service';
import { SnackbarService } from '../../services/design/snackbar.service';
import { AddCategoriesComponent } from '../product/add-categories/add-categories.component';
type StatusFilter = '' | 'true' | 'false';
interface Category {
  id: number;
  name: string;
  products: Category[];
  iconUrl?: string | null;
  status: 'Active' | 'Inactive';
  active: boolean;
  showInCategoryScreen: boolean;
  position: number;
  parentId: number | null;
  level: number;
}
interface ReorderScope {
  id: number;
  label: string;
  level: number;
}

@Component({
  selector: 'app-categories',
  standalone: true,
  imports: [CommonModule, DragDropModule, FormsModule, RouterModule],
  templateUrl: './categories.component.html',
  styleUrls: ['./categories.component.scss'],
})
export class CategoriesComponent implements OnInit {
  private categoriesService = inject(CategoriesService);
  private snackbar = inject(SnackbarService);
  readonly #modalService = inject(ModalService);
  private envInjector = inject(EnvironmentInjector);
  searchText = '';
  page = 1;
  pageSize = 7;
  total = 0;
  totalPages = 1;
  rowsPerPage = 3;
  sortBy = 'position';
  order: 'ASC' | 'DESC' = 'ASC';
  expandedIds = new Set<number>();
  statusFilter: StatusFilter = 'true';
  loading = false;
  reorderMode = false;
  reorderLoading = false;
  savingOrder = false;
  reorderParentId: number | null = null;
  reorderStatusFilter: StatusFilter = 'true';
  reorderTree: Category[] = [];
  reorderScopes: ReorderScope[] = [];

  categories: Category[] = [];
  productCounts: Record<number, CategoryProductCount> = {};
  countsLoading = false;
  countsError = false;
  visibilitySaving = new Set<number>();
  private loadSequence = 0;

  ngOnInit() {
    this.loadCategories();
  }
  toggleSort(column: 'name' | 'position') {
    if (this.sortBy === column) {
      this.order = this.order === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortBy = column;
      this.order = 'ASC';
    }
    this.loadCategories();
  }
  loadCategories() {
    const sequence = ++this.loadSequence;
    this.productCounts = {};
    this.countsLoading = true;
    this.countsError = false;
    this.loading = true;
    const isActive =
      this.statusFilter === 'true'
        ? true
        : this.statusFilter === 'false'
        ? false
        : undefined;

    this.categoriesService
      .getCategoryTree(
        this.page,
        this.pageSize,
        this.searchText,
        this.sortBy,
        this.order,
        isActive
      )
      .subscribe({
        next: (res) => {
          if (sequence !== this.loadSequence) return;
          this.categories = this.mapApiToUi(res.data);
          const collect = (nodes: CategoryDto[]): number[] => nodes.flatMap(node => [node.id, ...collect(node.children ?? [])]);
          const ids = collect(res.data);
          if (ids.length) {
            this.categoriesService.getProductCounts(ids).subscribe({
              next: counts => {
                if (sequence !== this.loadSequence) return;
                this.productCounts = Object.fromEntries(counts.map(count => [count.category_id, count]));
                this.countsLoading = false;
              },
              error: () => {
                if (sequence !== this.loadSequence) return;
                this.countsLoading = false;
                this.countsError = true;
              },
            });
          } else this.countsLoading = false;
          // console.log(this.categories, 'categories');
          const { page, total, totalPages } = res.meta;
          this.page = page;
          this.total = total;
          this.totalPages = totalPages;
          // console.log(totalPages, page, 'meta data');
          this.loading = false;
        },
        error: (err) => {
          if (sequence !== this.loadSequence) return;
          this.countsLoading = false;
          console.error('Failed to load categories', err);
          this.loading = false;
        },
      });
  }
  onFilterChange() {
    this.page = 1;
    this.loadCategories();
  }

  private mapApiToUi(data: CategoryDto[]): Category[] {
    return data.map((cat) => ({
      id: cat.id,
      name: cat.name,

      iconUrl: cat.iconUrl,
      position: cat.position,
      parentId: cat.parentId ?? null,
      level: cat.level,

      active: cat.isActive,
      showInCategoryScreen: cat.showInCategoryScreen !== false,
      status: cat.isActive ? 'Active' : 'Inactive',

      products: cat.children ? this.mapApiToUi(cat.children) : [],
    }));
  }

  toggleCategory(cat: Category) {
    const previousActive = cat.active;
    const previousStatus = cat.status;

    // Optimistic UI update
    cat.active = !cat.active;
    cat.status = cat.active ? 'Active' : 'Inactive';

    this.categoriesService.toggleCategory(cat.id).subscribe({
      next: (updatedNode) => {
        // 🔥 apply backend truth
        console.log(updatedNode, 'updatedNode');
        this.applyUpdatedNode(cat, updatedNode);
      },
      error: () => {
        // ❌ rollback UI
        cat.active = previousActive;
        cat.status = previousStatus;
      },
    });
  }

  setCategoryScreenVisibility(cat: Category, event?: Event) {
    // Keep the switch on persisted truth until the server confirms the change.
    if (event) (event.target as HTMLInputElement).checked = cat.showInCategoryScreen;
    if (this.visibilitySaving.has(cat.id)) return;
    this.visibilitySaving.add(cat.id);
    this.categoriesService.setCategoryScreenVisibility(cat.id, !cat.showInCategoryScreen).subscribe({
      next: updated => {
        const apply = (nodes: Category[]) => nodes.forEach(node => {
          if (node.id === cat.id) node.showInCategoryScreen = updated.showInCategoryScreen !== false;
          apply(node.products);
        });
        apply(this.categories); apply(this.reorderTree);
        cat.showInCategoryScreen = updated.showInCategoryScreen !== false;
        this.visibilitySaving.delete(cat.id);
        this.snackbar.success('Customer Categories screen visibility saved');
      },
      error: error => {
        this.visibilitySaving.delete(cat.id);
        this.snackbar.error(error?.message || 'Could not save category visibility');
      },
    });
  }

  hiddenByParent(cat: Category): boolean {
    const find = (nodes: Category[], parentHidden = false): boolean | undefined => {
      for (const node of nodes) {
        if (node.id === cat.id) return parentHidden;
        const result = find(node.products, parentHidden || !node.showInCategoryScreen);
        if (result !== undefined) return result;
      }
      return undefined;
    };
    return find(this.categories) ?? false;
  }

  applyUpdatedNode(target: Category, updated: CategoryDto) {
    // update target itself
    target.active = updated.isActive;
    target.status = updated.isActive ? 'Active' : 'Inactive';

    // 🔥 if parent category → cascade to all children
    if (target.products?.length) {
      this.updateChildrenStatus(target.products, updated.isActive);
    }
  }
  private updateChildrenStatus(children: Category[], isActive: boolean) {
    children.forEach((child) => {
      child.active = isActive;
      child.status = isActive ? 'Active' : 'Inactive';

      if (child.products?.length) {
        this.updateChildrenStatus(child.products, isActive);
      }
    });
  }

  /* ---------------- UI logic (unchanged) ---------------- */

  isExpanded(id: number) {
    return this.expandedIds.has(id);
  }
  setStatus(value: StatusFilter) {
    this.statusFilter = value;
    this.page = 1;
    this.loadCategories();
  }

  toggleReorderMode() {
    this.reorderMode = !this.reorderMode;
    if (this.reorderMode) this.loadReorderTree();
  }

  loadReorderTree() {
    this.reorderLoading = true;
    this.categoriesService.getCategoryTreeAll(false).subscribe({
      next: (data) => {
        this.reorderTree = this.mapApiToUi(data);
        this.reorderScopes = this.buildReorderScopes(this.reorderTree);
        this.reorderLoading = false;
      },
      error: () => {
        this.reorderLoading = false;
        this.snackbar.error('Failed to load category order');
      },
    });
  }

  get reorderItems(): Category[] {
    if (this.reorderParentId == null) return this.reorderTree;
    return this.findCategory(this.reorderTree, this.reorderParentId)?.products ?? [];
  }

  get reorderVisibleItems(): Category[] {
    if (this.reorderStatusFilter === '') return this.reorderItems;
    const active = this.reorderStatusFilter === 'true';
    return this.reorderItems.filter((category) => category.active === active);
  }

  setReorderStatus(value: StatusFilter) {
    if (this.savingOrder) return;
    this.reorderStatusFilter = value;
  }

  onCategoryDrop(event: CdkDragDrop<Category[]>) {
    if (event.previousIndex === event.currentIndex || this.savingOrder) return;

    const visibleItems = event.container.data;
    moveItemInArray(visibleItems, event.previousIndex, event.currentIndex);

    const fullItems = this.reorderItems;
    const visibleIds = new Set(visibleItems.map((category) => category.id));
    let visibleIndex = 0;
    const reorderedItems = fullItems.map((category) =>
      visibleIds.has(category.id) ? visibleItems[visibleIndex++] : category
    );
    reorderedItems.forEach((category, index) => (category.position = index));
    fullItems.splice(0, fullItems.length, ...reorderedItems);
    this.savingOrder = true;

    this.categoriesService
      .updateCategoryPositions(
        reorderedItems.map(({ id, position }) => ({ id, position }))
      )
      .subscribe({
        next: () => {
          this.savingOrder = false;
          this.loadCategories();
          this.snackbar.success('Category order updated');
        },
        error: () => {
          this.savingOrder = false;
          this.loadReorderTree();
          this.loadCategories();
          this.snackbar.error('Failed to update category order');
        },
      });
  }

  reorderScopeLabel(): string {
    if (this.reorderParentId == null) return 'Root categories (L0)';
    return (
      this.reorderScopes.find((scope) => scope.id === this.reorderParentId)
        ?.label ?? 'Selected category'
    );
  }

  private buildReorderScopes(
    categories: Category[],
    parentPath = ''
  ): ReorderScope[] {
    return categories.flatMap((category) => {
      const path = parentPath ? `${parentPath} > ${category.name}` : category.name;
      const current =
        category.level < 2
          ? [{ id: category.id, label: path, level: category.level }]
          : [];

      return [
        ...current,
        ...this.buildReorderScopes(category.products, path),
      ];
    });
  }

  private findCategory(categories: Category[], id: number): Category | null {
    for (const category of categories) {
      if (category.id === id) return category;
      const match = this.findCategory(category.products, id);
      if (match) return match;
    }
    return null;
  }

  toggleExpand(id: number) {
    if (this.expandedIds.has(id)) {
      this.expandedIds.delete(id);
    } else {
      this.expandedIds.add(id);
    }
  }

  editCategory(cat: Category) {
    console.log(this.categories, 'categories');
    console.log(cat, 'selected Categories');
    this.#modalService
      .open(AddCategoriesComponent, { modalData: cat })
      .subscribe((result) => {
        this.loadCategories();
      });
  }

  deleteCategory(cat: Category) {
    alert(`Delete category: ${cat.name}`);
  }

  get filteredCategories() {
    return this.categories;
  }

  changePage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.page = page;
    this.loadCategories();
  }

  goToPage(page: number) {
    this.changePage(page);
  }
  createCategories() {
    this.#modalService.open(AddCategoriesComponent).subscribe((result) => {
      this.loadCategories();
    });
  }
  get fromCategory() {
    if (!this.total) return 0;
    return (this.page - 1) * this.pageSize + 1;
  }

  get toCategory() {
    return Math.min(this.page * this.pageSize, this.total);
  }
}
